/**
 * 服务管理工具函数
 * 包含数据处理、状态计算、表单处理、验证、日志等工具函数
 */

import { ServiceRecord } from "../service-apis/serviceTypes";
import { message } from "antd";

const normalizeServiceStatus = (status: string | undefined) => {
  if (!status) {
    return undefined;
  }
  const statusMap: Record<string, string> = {
    running: "online",
    stopped: "offline",
    online: "online",
    offline: "offline",
    maintenance: "maintenance",
  };
  return statusMap[status] || status;
};

const formatDateValue = (value: any) => {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "object" && typeof value.format === "function") {
    return value.format("YYYY-MM-DD HH:mm:ss");
  }
  return value;
};

const normalizeSearchParams = (params: Record<string, any>) => {
  const normalized: Record<string, any> = {};

  if (params.service_name !== undefined) {
    normalized.service_name = params.service_name;
    if (typeof params.service_name === "string" && params.service_name.trim()) {
      normalized.name = params.service_name.trim();
    }
  } else if (params.name !== undefined && typeof params.name === "string" && params.name.trim()) {
    normalized.name = params.name.trim();
  }

  if (params.status !== undefined && params.status !== null && params.status !== "") {
    normalized.status = normalizeServiceStatus(params.status);
  }

  if (params.description !== undefined && params.description !== null && params.description !== "") {
    normalized.description = typeof params.description === "string" ? params.description.trim() : params.description;
  }

  if (Array.isArray(params.created_at) && params.created_at.length === 2) {
    const [start, end] = params.created_at;
    normalized.created_at = params.created_at;
    const formattedStart = formatDateValue(start);
    const formattedEnd = formatDateValue(end);
    if (formattedStart) {
      normalized.created_at_start = formattedStart;
    }
    if (formattedEnd) {
      normalized.created_at_end = formattedEnd;
    }
  }

  return normalized;
};

// 计算服务统计数据
export const calculateServiceStats = (serviceList: ServiceRecord[]) => {
  return {
    total_services: serviceList.length,
    running_services: serviceList.filter((s) => s.status === "online").length,
    stopped_services: serviceList.filter((s) => s.status === "offline").length,
    maintenance_services: serviceList.filter((s) => s.status === "maintenance").length,
  };
};

// 处理搜索参数
export const processSearchParams = (
  currentParams: Record<string, any>,
  newParams: Record<string, any>
) => {
  const mergedParams = { ...currentParams, ...newParams };
  return normalizeSearchParams(mergedParams);
};

// 处理分页参数
export const processPaginationParams = (
  pagination: { current: number; pageSize: number },
  searchParams: Record<string, any>
) => {
  const apiSearchParams = normalizeSearchParams(searchParams);
  return {
    current: pagination.current,
    pageSize: pagination.pageSize,
    ...apiSearchParams,
  };
};

// 重置搜索参数
export const resetSearchParams = () => {
  return {};
};

// 重置分页参数
export const resetPaginationParams = () => {
  return {
    current: 1,
    pageSize: 10,
  };
};

// 处理分页变化
export const handlePaginationChange = (
  paginationInfo: any,
  currentSearchParams: Record<string, any>
) => {
  return {
    current: paginationInfo.current,
    pageSize: paginationInfo.pageSize,
    ...currentSearchParams,
  };
};

// 验证健康检查URL
export const validateHealthCheckUrl = (record: ServiceRecord): boolean => {
  // 如果配置了健康检查URL，则可以使用
  if (record.healthCheckUrl && record.healthCheckUrl.trim()) {
    return true;
  }
  // 如果没有配置健康检查URL，但配置了后端地址，可以使用默认的/health
  if (record.backendUrl && record.backendUrl.trim()) {
    return true;
  }
  // 都没有配置，无法进行健康检查
  return false;
};

// 处理Token表单初始值
export const getTokenFormInitialValues = (tokenInfo: any) => {
  // 导入 dayjs
  const dayjs = require("dayjs");

  if (!tokenInfo) {
    return {
      token_value: "",
      expires_at: undefined,
      is_active: true,
      max_usage: undefined,
      ip_whitelist: "",
      description: "",
      allowed_audience: [],
    };
  }

  // 处理 expires_at：转换为 dayjs 对象（因为表单字段是 date 类型，DatePicker 需要 dayjs 对象）
  let expiresAt: any = undefined;
  if (tokenInfo.expires_at || tokenInfo.expiresAt) {
    const expiresAtValue = tokenInfo.expires_at || tokenInfo.expiresAt;
    if (typeof expiresAtValue === "string" && expiresAtValue.trim()) {
      // 字符串转换为 dayjs 对象
      expiresAt = dayjs(expiresAtValue.trim());
    } else if (expiresAtValue && typeof expiresAtValue.format === "function") {
      // 如果已经是 dayjs 对象，直接使用
      expiresAt = expiresAtValue;
    } else if (expiresAtValue) {
      // 其他类型，尝试转换为 dayjs 对象
      expiresAt = dayjs(expiresAtValue);
    }
  }

  // 处理 allowed_audience：确保是数组格式
  const allowedAudience = tokenInfo.allowed_audience || tokenInfo.allowedAudience || [];
  const normalizedAllowedAudience = Array.isArray(allowedAudience)
    ? allowedAudience.filter((audience: any) => typeof audience === "string" && audience.trim().length > 0)
    : [];

  console.log("🔍 [getTokenFormInitialValues] 处理allowed_audience:", {
    tokenInfo,
    allowed_audience_raw: tokenInfo.allowed_audience,
    allowedAudience_raw: tokenInfo.allowedAudience,
    allowedAudience,
    normalizedAllowedAudience,
  });

  return {
    token_value: tokenInfo.token_value || tokenInfo.tokenValue || "",
    expires_at: expiresAt,
    is_active: tokenInfo.is_active !== undefined ? tokenInfo.is_active : tokenInfo.isActive ?? true,
    max_usage: tokenInfo.max_usage ?? tokenInfo.maxUsage ?? undefined,
    ip_whitelist: tokenInfo.ip_whitelist ?? tokenInfo.ipWhitelist ?? "",
    description: tokenInfo.description ?? "",
    allowed_audience: normalizedAllowedAudience,
  };
};

// 处理编辑表单初始值
export const getEditFormInitialValues = (currentRecord: ServiceRecord | null) => {
  if (!currentRecord) {
    return undefined;
  }

  const statusValue = (() => {
    if (currentRecord.service_status !== undefined) {
      return currentRecord.service_status;
    }
    switch (currentRecord.status) {
      case "online":
        return 1;
      case "offline":
        return 2;
      case "maintenance":
        return 3;
      default:
        return undefined;
    }
  })();

  // 规范化 allowed_audience 数据格式
  const normalizeAllowedAudience = (value: any): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.filter((item) => item !== null && item !== undefined && item !== "");
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.filter((item) => item !== null && item !== undefined && item !== "");
        }
      } catch {
        return value.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
      }
    }
    return [];
  };

  const rawValue = currentRecord.allowed_audience;
  const allowedAudienceValue = normalizeAllowedAudience(rawValue);

  console.log("[getEditFormInitialValues] 处理编辑表单初始值:", {
    serviceName: currentRecord.name,
    rawAllowedAudience: rawValue,
    allowed_audience: currentRecord.allowed_audience,
    finalAllowedAudience: allowedAudienceValue,
    isArray: Array.isArray(rawValue),
    type: typeof rawValue,
  });

  // 处理 required_permissions 字段：确保是字符串格式
  const normalizeRequiredPermissions = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string") {
      return value;
    }
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    return "";
  };

  const recordAny = currentRecord as any;
  const requiredPermissionsValue = normalizeRequiredPermissions(
    recordAny.required_permissions ||
    recordAny.requiredPermissions ||
    recordAny.permissions
  );

  // service_type 默认值处理：优先使用已有值，未设置时不强行改成系统类型
  const serviceTypeValueRaw =
    recordAny.service_type ?? recordAny.serviceType ?? recordAny.service_type;
  const serviceTypeValue =
    typeof serviceTypeValueRaw === "number"
      ? serviceTypeValueRaw
      : serviceTypeValueRaw !== undefined && serviceTypeValueRaw !== null && serviceTypeValueRaw !== ""
        ? Number.parseInt(String(serviceTypeValueRaw), 10)
        : undefined;

  return {
    ...currentRecord,
    display_name: currentRecord.displayName ?? currentRecord.display_name,
    service_description: currentRecord.description ?? currentRecord.service_description,
    icon_name: currentRecord.icon ?? currentRecord.icon_name,
    icon: currentRecord.icon ?? currentRecord.icon_name,
    route_path: currentRecord.routePath ?? currentRecord.route_path,
    menu_order: currentRecord.menuOrder ?? currentRecord.menu_order,
    backendUrl: currentRecord.backendUrl ?? currentRecord.service_url,
    url: currentRecord.url ?? currentRecord.service_endpoint,
    health_check_url: currentRecord.healthCheckUrl ?? currentRecord.health_check_url,
    is_menu_item: currentRecord.isMenuItem ?? currentRecord.is_menu_item,
    requires_auth: currentRecord.requiresAuth ?? currentRecord.requires_auth,
    is_external: currentRecord.isExternal ?? currentRecord.external ?? currentRecord.is_external,
    // 同时提供蛇形与驼峰，避免表单字段名不一致导致回填失败
    service_type:
      serviceTypeValue !== undefined && !Number.isNaN(serviceTypeValue)
        ? serviceTypeValue
        : undefined,
    serviceType:
      serviceTypeValue !== undefined && !Number.isNaN(serviceTypeValue)
        ? serviceTypeValue
        : undefined,
    service_status: statusValue,
    allowed_audience: allowedAudienceValue,
    required_permissions: requiredPermissionsValue,
    requiredPermissions: requiredPermissionsValue,
    permissions: requiredPermissionsValue,
    service_secret: (currentRecord as any).service_secret ?? "",
  };
};

// 处理Token操作结果
export const handleTokenOperationResult = (
  success: boolean,
  onSuccess: () => void
) => {
  if (success) {
    onSuccess();
  }
};

// 处理服务操作结果
export const handleServiceOperationResult = (
  success: boolean,
  onSuccess: () => void
) => {
  if (success) {
    onSuccess();
  }
};

// 创建加载消息配置
export const createLoadingMessageConfig = (content: string, key: string) => {
  return {
    content,
    key,
    duration: 0,
  };
};

// 处理健康检查结果
export const handleHealthCheckResult = (
  success: boolean,
  onSuccess: () => void,
  onError: (error: any) => void,
  error?: any
) => {
  if (success) {
    onSuccess();
  } else if (error) {
    onError(error);
  }
};

// 格式化服务状态
export const formatServiceStatus = (status: string): string => {
  const statusMap = {
    "online": "可用",
    "offline": "不可用",
    "maintenance": "维护中",
  };
  return statusMap[status as keyof typeof statusMap] || "未知状态";
};

// 格式化服务类型
export const formatServiceType = (type: string): string => {
  const typeMap = {
    "frontend": "前端服务",
    "backend": "后端服务",
    "database": "数据库服务",
    "cache": "缓存服务",
    "queue": "队列服务",
  };
  return typeMap[type as keyof typeof typeMap] || type;
};

// 检查服务是否可操作
export const canOperateService = (record: ServiceRecord): boolean => {
  return record.status !== "maintenance"; // 维护中的服务不可操作
};

// 检查服务是否需要健康检查
export const needsHealthCheck = (record: ServiceRecord): boolean => {
  return validateHealthCheckUrl(record);
};

// 生成服务操作日志
export const generateServiceOperationLog = (
  operation: string,
  record: ServiceRecord,
  result: boolean
): string => {
  const timestamp = new Date().toISOString();
  const status = result ? "成功" : "失败";
  return `[${timestamp}] ${operation}服务"${record.name}"${status}`;
};

// ==================== 验证和操作工具函数 ====================

/**
 * 验证服务记录
 */
export const validateServiceRecord = (record: ServiceRecord): boolean => {
  if (!record) {
    console.error("[validateServiceRecord] 服务记录不存在");
    message.error("服务记录不存在");
    return false;
  }
  if (!record.id) {
    console.error("[validateServiceRecord] 服务ID不存在:", {
      record,
      hasId: !!record.id,
      idValue: record.id,
      recordKeys: Object.keys(record)
    });
    message.error("服务ID不存在，无法执行操作");
    return false;
  }
  console.log("[validateServiceRecord] 验证通过:", { id: record.id, name: record.name });
  return true;
};

/**
 * 生成操作日志（通用版本）
 */
export const generateOperationLog = (
  operation: string,
  record: ServiceRecord,
  result: boolean
): string => {
  const timestamp = new Date().toISOString();
  const status = result ? "成功" : "失败";
  return `[${timestamp}] ${operation}服务"${record.name}"${status}`;
};

/**
 * 处理批量操作
 */
export const handleBatchOperation = async (
  records: ServiceRecord[],
  operation: (record: ServiceRecord) => Promise<boolean>,
  operationName: string
): Promise<{ success: number; failed: number }> => {
  let success = 0;
  let failed = 0;

  for (const record of records) {
    try {
      const result = await operation(record);
      if (result) {
        success++;
      } else {
        failed++;
      }
    } catch (error) {
      console.error(`${operationName}失败:`, error);
      failed++;
    }
  }

  message.info(`${operationName}完成: 成功 ${success} 个，失败 ${failed} 个`);
  return { success, failed };
};
